import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Calendar, Clock, MapPin } from 'lucide-react';
import { Box, Fade, Typography } from '@mui/material';

interface Event {
  id: string;
  title: string;
  date: Date;
  type: 'exam' | 'assignment' | 'holiday' | 'meeting' | 'activity';
  time?: string;
  location?: string;
  description?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: Event[];
}

const SchoolCalendar: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [events, setEvents] = useState<Event[]>([
    {
      id: '1',
      title: 'Math Quiz',
      date: new Date(2025, 5, 15),
      type: 'exam',
      time: '10:00 AM',
      location: 'Room 204',
      description: 'Algebra and Geometry quiz'
    },
    {
      id: '2',
      title: 'Science Fair',
      date: new Date(2025, 5, 20),
      type: 'activity',
      time: '2:00 PM',
      location: 'Gymnasium',
      description: 'Annual school science fair exhibition'
    },
    {
      id: '3',
      title: 'Summer Break',
      date: new Date(2025, 5, 25),
      type: 'holiday',
      description: 'Last day of school'
    },
    {
      id: '4',
      title: 'Essay Submission',
      date: new Date(2025, 5, 12),
      type: 'assignment',
      time: '11:59 PM',
      description: 'English literature essay due'
    }
  ]);

  const [newEvent, setNewEvent] = useState<Partial<Event>>({
    title: '',
    type: 'assignment',
    time: '',
    location: '',
    description: ''
  });

  const eventColors = {
    exam: 'bg-red-100 text-red-800 border-red-200',
    assignment: 'bg-blue-100 text-blue-800 border-blue-200',
    holiday: 'bg-green-100 text-green-800 border-green-200',
    meeting: 'bg-purple-100 text-purple-800 border-purple-200',
    activity: 'bg-orange-100 text-orange-800 border-orange-200'
  };

  const eventIcons = {
    exam: '📝',
    assignment: '📚',
    holiday: '🎉',
    meeting: '👥',
    activity: '🎯'
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days: CalendarDay[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const dayEvents = events.filter(event => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);
        return eventDate.getTime() === compareDate.getTime();
      });

      days.push({
        date: new Date(date),
        isCurrentMonth: date.getMonth() === month,
        isToday: date.getTime() === today.getTime(),
        events: dayEvents
      });
    }

    return days;
  }, [currentDate, events]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const handleAddEvent = () => {
    if (!selectedDate || !newEvent.title) return;

    const event: Event = {
      id: Date.now().toString(),
      title: newEvent.title,
      date: selectedDate,
      type: newEvent.type as Event['type'],
      time: newEvent.time,
      location: newEvent.location,
      description: newEvent.description
    };

    setEvents(prev => [...prev, event]);
    setNewEvent({ title: '', type: 'assignment', time: '', location: '', description: '' });
    setShowEventModal(false);
  };

  const handleDeleteEvent = (eventId: string) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return events
      .filter(event => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime())
      .slice(0, 5);
  };

  return (
    <div className="w-full min-h-screen flex justify-center items-center m-auto">
      <div className="md:w-[80%] w-[90%]   overflow-hidden">
        {/* Header */}
        <Fade in timeout={800}>
            <Box className="text-center mb-12">
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4rem' },
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                School Calendar
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1.1rem',
                  maxWidth: '600px',
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontWeight: 400
                }}
              >
                Here you can add events and view the calendar.
              </Typography>
            </Box>
          </Fade>
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Calendar className="w-8 h-8" />
              <h1 className="text-3xl font-bold">School Calendar</h1>
            </div>
            <div className="text-right">
              <div className="text-blue-100">Academic Year 2024-2025</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 p-6">
          {/* Main Calendar */}
          <div className="lg:col-span-3">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <h2 className="text-2xl font-bold text-gray-800">
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              
              <button
                onClick={() => navigateMonth('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Weekday Headers */}
              <div className="grid grid-cols-7 bg-gray-50">
                {weekdays.map(day => (
                  <div key={day} className="p-3 text-center font-semibold text-gray-700 border-r border-gray-200 last:border-r-0">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7">
                {calendarDays.map((day, index) => (
                  <div
                    key={index}
                    onClick={() => handleDateClick(day.date)}
                    className={`min-h-[120px] p-2 border-r border-b border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors ${
                      !day.isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
                    } ${day.isToday ? 'bg-blue-100 ring-2 ring-blue-500' : ''}`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-sm font-medium ${day.isToday ? 'text-blue-600' : ''}`}>
                        {day.date.getDate()}
                      </span>
                      {day.events.length > 0 && (
                        <span className="text-xs bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center">
                          {day.events.length}
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1">
                      {day.events.slice(0, 2).map(event => (
                        <div
                          key={event.id}
                          className={`text-xs p-1 rounded border ${eventColors[event.type]} truncate`}
                          title={event.title}
                        >
                          {eventIcons[event.type]} {event.title}
                        </div>
                      ))}
                      {day.events.length > 2 && (
                        <div className="text-xs text-gray-500">
                          +{day.events.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Add Event Button */}
            <button
              onClick={() => {
                setSelectedDate(selectedDate || new Date());
                setShowEventModal(true);
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center justify-center space-x-2 shadow-lg"
            >
              <Plus className="w-5 h-5" />
              <span>Add Event</span>
            </button>

            {/* Selected Date */}
            {selectedDate && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Selected Date</h3>
                <p className="text-blue-600">
                  {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                
                {selectedDate && events.filter(event => {
                  const eventDate = new Date(event.date);
                  eventDate.setHours(0, 0, 0, 0);
                  const compareDate = new Date(selectedDate);
                  compareDate.setHours(0, 0, 0, 0);
                  return eventDate.getTime() === compareDate.getTime();
                }).length > 0 && (
                  <div className="mt-3 space-y-2">
                    <h4 className="text-sm font-medium text-blue-700">Events:</h4>
                    {events.filter(event => {
                      const eventDate = new Date(event.date);
                      eventDate.setHours(0, 0, 0, 0);
                      const compareDate = new Date(selectedDate);
                      compareDate.setHours(0, 0, 0, 0);
                      return eventDate.getTime() === compareDate.getTime();
                    }).map(event => (
                      <div key={event.id} className={`text-xs p-2 rounded border ${eventColors[event.type]} flex justify-between items-center`}>
                        <div>
                          <div className="font-medium">{eventIcons[event.type]} {event.title}</div>
                          {event.time && <div className="text-gray-600">{event.time}</div>}
                        </div>
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Upcoming Events */}
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Upcoming Events
              </h3>
              <div className="space-y-3">
                {getUpcomingEvents().map(event => (
                  <div key={event.id} className={`p-3 rounded-lg border ${eventColors[event.type]}`}>
                    <div className="font-medium text-sm">{eventIcons[event.type]} {event.title}</div>
                    <div className="text-xs text-gray-600 mt-1">
                      {event.date.toLocaleDateString()}
                      {event.time && ` • ${event.time}`}
                    </div>
                    {event.location && (
                      <div className="text-xs text-gray-600 flex items-center mt-1">
                        <MapPin className="w-3 h-3 mr-1" />
                        {event.location}
                      </div>
                    )}
                  </div>
                ))}
                {getUpcomingEvents().length === 0 && (
                  <p className="text-gray-500 text-sm">No upcoming events</p>
                )}
              </div>
            </div>

            {/* Legend */}
            <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">Event Types</h3>
              <div className="space-y-2">
                {Object.entries(eventColors).map(([type, color]) => (
                  <div key={type} className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded border ${color}`}></div>
                    <span className="text-sm capitalize">{eventIcons[type as keyof typeof eventIcons]} {type}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add New Event</h3>
              <button
                onClick={() => setShowEventModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newEvent.title || ''}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Event title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newEvent.type || 'assignment'}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, type: e.target.value as Event['type'] }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="assignment">📚 Assignment</option>
                  <option value="exam">📝 Exam</option>
                  <option value="holiday">🎉 Holiday</option>
                  <option value="meeting">👥 Meeting</option>
                  <option value="activity">🎯 Activity</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="text"
                  value={newEvent.time || ''}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 2:00 PM"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={newEvent.location || ''}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Event location"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newEvent.description || ''}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Event description"
                />
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowEventModal(false)}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEvent}
                disabled={!newEvent.title}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolCalendar;